import { Dispatch, FC, SetStateAction, useState } from 'react'
import { Box, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'

/* Utils */
import {
  CustomLabelCountI,
  CustomLabelDate,
  CustomLabelI,
  CustomLabelsDataPayload,
  CustomLabelsListI,
  DateRangeI,
  SystemEventName,
  VisibleLabel
} from 'models'
import { dateFormat } from 'lib/utils'
import { fillDatesBetweenRange } from 'ui/pages/library/utils'

/* Components */
import { LabelsShowModeWrapper, CreateLabelWrapper } from './labelsShowMode.styles'
import { Button } from 'ui/components/buttons'
import LabelFilters from '../components/LabelFilters'
import DrawerLabelCard from '../components/drawer-label-card/DrawerLabelCard'

interface Props {
  setLoading: Dispatch<SetStateAction<boolean>>
  customLabels: CustomLabelsListI
  customLabelsCount: CustomLabelCountI
  updateEditLabel: (label: any) => void
  updateMode: (newMode: string) => void
  updateLabels: (payload?: CustomLabelsDataPayload, ignoreCustomPeriod?: boolean) => Promise<void>
  mode: string
  loading: boolean
  customDate: CustomLabelDate
  unpinLabel: (labelId: string) => Promise<void>
  pinLabel: (labelId: string) => Promise<void>
  visibleLabels: VisibleLabel[]
  setVisibleLabels: Dispatch<SetStateAction<VisibleLabel[]>>
  pinningLabel: boolean
  setPinningLabel: Dispatch<SetStateAction<boolean>>
  dateRange: DateRangeI
  selectedDateRange: [Dayjs | null, Dayjs | null]
  setSelectedDateRange: (newRange: [Dayjs | null, Dayjs | null]) => void
}

const LabelsShowMode: FC<Props> = ({
  customLabels,
  updateMode,
  setLoading,
  loading,
  updateEditLabel,
  customLabelsCount,
  updateLabels,
  mode,
  customDate,
  unpinLabel,
  pinLabel,
  pinningLabel,
  setPinningLabel,
  dateRange,
  selectedDateRange,
  setSelectedDateRange
}) => {
  const [selectedLabelTypes, setSelectedLabelTypes] = useState<string[]>([])

  const getFilteredLabels = () => {
    // When we have fresh data after creation, don't apply date filtering
    if (!dateRange.minDate || !dateRange.maxDate) {
      return customLabels
    }

    const filteredLabels: CustomLabelsListI = {}
    const startDate = dayjs(dateRange.minDate)
    const endDate = dayjs(dateRange.maxDate)

    Object.keys(customLabels).forEach((date) => {
      const currentDate = dayjs(date)
      // Make the date comparison more inclusive
      if (currentDate.isBetween(startDate, endDate, 'day', '[]')) {
        filteredLabels[date] = customLabels[date]
      }
    })

    return filteredLabels
  }

  const filteredLabels = getFilteredLabels()

  const renderCustomDateRange = () => {
    const filledDates = fillDatesBetweenRange(new Date(customDate.start), new Date(customDate.end))
    const containedDates = filledDates.map((n: string | number | Date | dayjs.Dayjs | null | undefined) =>
      dayjs(n).format(dateFormat.customLabel)
    )

    return Object.keys(filteredLabels)
      .filter((n) => containedDates.includes(n))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map((date, i) => {
        const labelsArray = filteredLabels[date].filter((label: CustomLabelI) => {
          if (selectedLabelTypes.length === 0) {
            return true
          }

          const actionToFilterMap: { [key: string]: string } = {
            [SystemEventName.SlaChangedEvent]: 'sla_changed',
            [SystemEventName.CpiActivated]: 'cpi_activated',
            [SystemEventName.CpiInstanceAdded]: 'instance_added',
            [SystemEventName.CpiInstanceRemoved]: 'instance_removed',
            [SystemEventName.CpiCustom]: 'custom_label'
          }

          const filterValue = actionToFilterMap[label.action]

          return selectedLabelTypes.includes(filterValue)
        })

        if (!labelsArray.length) {
          if (i === 0) {
            return <Typography className="info">No labels in this time period.</Typography>
          } else return null
        }

        return (
          <>
            <Typography className="date">{dayjs(date).format(dateFormat.short)}</Typography>

            {labelsArray.map((label: CustomLabelI) => {
              return (
                <DrawerLabelCard
                  key={label.id}
                  updateMode={updateMode}
                  label={label}
                  customLabelsCount={customLabelsCount}
                  updateEditLabel={updateEditLabel}
                  updateLabels={updateLabels}
                  unpinLabel={unpinLabel}
                  pinLabel={pinLabel}
                  pinningLabel={pinningLabel}
                  setPinningLabel={setPinningLabel}
                  setLoading={setLoading}
                />
              )
            })}
          </>
        )
      })
  }

  return (
    <LabelsShowModeWrapper>
      <LabelFilters
        selectedLabelTypes={selectedLabelTypes}
        setSelectedLabelTypes={setSelectedLabelTypes}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
      />

      <Box className="show-mode-content">
        {!loading && !Object.keys(filteredLabels).length ? (
          <Typography className="info">No labels in this time period.</Typography>
        ) : !loading ? (
          <>
            {/* Render list of all Labels and System Events */}
            {Object.keys(filteredLabels).length &&
              mode === 'show-0' &&
              Object.keys(filteredLabels)
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                .map((date, i) => {
                  const labelsArray = filteredLabels[date].filter((label: CustomLabelI) => {
                    if (selectedLabelTypes.length === 0) {
                      return true
                    }

                    const actionToFilterMap: { [key: string]: string } = {
                      [SystemEventName.SlaChangedEvent]: 'sla_changed',
                      [SystemEventName.CpiActivated]: 'cpi_activated',
                      [SystemEventName.CpiInstanceAdded]: 'instance_added',
                      [SystemEventName.CpiInstanceRemoved]: 'instance_removed',
                      [SystemEventName.CpiCustom]: 'custom_label'
                    }

                    const filterValue = actionToFilterMap[label.action]

                    return selectedLabelTypes.includes(filterValue)
                  })

                  if (labelsArray.length === 0 && i === 0)
                    return <Typography className="info">No labels in this time period.</Typography>

                  return labelsArray.map((label: CustomLabelI) => (
                    <>
                      <DrawerLabelCard
                        label={label}
                        updateLabels={updateLabels}
                        customLabelsCount={customLabelsCount}
                        unpinLabel={unpinLabel}
                        pinLabel={pinLabel}
                        pinningLabel={pinningLabel}
                        setPinningLabel={setPinningLabel}
                        updateEditLabel={updateEditLabel}
                        updateMode={updateMode}
                        setLoading={setLoading}
                      />
                    </>
                  ))
                })}

            {/* Render list of Labels and System Events for selected chart stripe single date */}
            {Object.keys(filteredLabels).length &&
              mode === 'show-date' &&
              customDate.start.length > 0 &&
              !customDate.end.length && (
                <>
                  {filteredLabels[customDate.start]?.length &&
                    filteredLabels[customDate.start]
                      .filter((label: CustomLabelI) => {
                        if (selectedLabelTypes.length === 0) {
                          return true
                        }

                        const actionToFilterMap: { [key: string]: string } = {
                          [SystemEventName.SlaChangedEvent]: 'sla_changed',
                          [SystemEventName.CpiActivated]: 'cpi_activated',
                          [SystemEventName.CpiInstanceAdded]: 'instance_added',
                          [SystemEventName.CpiInstanceRemoved]: 'instance_removed',
                          [SystemEventName.CpiCustom]: 'custom_label'
                        }

                        const filterValue = actionToFilterMap[label.action]

                        return selectedLabelTypes.includes(filterValue)
                      })
                      .map((label: CustomLabelI) => {
                        return (
                          <DrawerLabelCard
                            setLoading={setLoading}
                            key={label.id}
                            updateMode={updateMode}
                            label={label}
                            customLabelsCount={customLabelsCount}
                            updateEditLabel={updateEditLabel}
                            updateLabels={updateLabels}
                            unpinLabel={unpinLabel}
                            pinLabel={pinLabel}
                            pinningLabel={pinningLabel}
                            setPinningLabel={setPinningLabel}
                          />
                        )
                      })}
                </>
              )}

            {/* Render list of Labels and System Events for selected chart stripe date range */}
            {Object.keys(filteredLabels).length &&
              mode === 'show-date' &&
              customDate.start.length > 0 &&
              customDate.end.length > 0 &&
              renderCustomDateRange()}
          </>
        ) : (
          ''
        )}
      </Box>

      <CreateLabelWrapper>
        <Button
          themevariant="hollow"
          type="button"
          className="create-label-button"
          onClick={() => updateMode('create')}
        >
          Create Custom Label
        </Button>
      </CreateLabelWrapper>
    </LabelsShowModeWrapper>
  )
}

export default LabelsShowMode
